import {useCallback, useEffect, useRef, useState} from 'react'

export type UseResizeObserverOptions<T extends Element> = {
  ref?: React.MutableRefObject<T | null>
  box?: ResizeObserverBoxOptions
  onResize?: (size: {width: number; height: number}) => void
}

export type UseResizeObserverResult<T extends Element> = {
  ref: (element: T | null) => void
  width: number | undefined
  height: number | undefined
}

export function useResizeObserver<T extends Element = HTMLElement>(
  options: UseResizeObserverOptions<T> = {}
): UseResizeObserverResult<T> {
  const {ref: externalRef, box, onResize} = options

  const [size, setSize] = useState<{width: number | undefined; height: number | undefined}>({
    width: undefined,
    height: undefined
  })

  const observerRef = useRef<ResizeObserver | null>(null)

  const onResizeRef = useRef(onResize)
  useEffect(() => {
    onResizeRef.current = onResize
  }, [onResize])

  const ref = useCallback(
    (element: T | null) => {
      if (externalRef) externalRef.current = element

      observerRef.current?.disconnect()
      observerRef.current = null

      if (!element) return

      const observer = new ResizeObserver((entries) => {
        const entry = entries[0]
        if (!entry) return

        const borderBox = entry.borderBoxSize?.[0]
        const useBorderBox = box === 'border-box' && borderBox !== undefined
        const width = useBorderBox ? borderBox.inlineSize : entry.contentRect.width
        const height = useBorderBox ? borderBox.blockSize : entry.contentRect.height

        setSize((prev) => (prev.width === width && prev.height === height ? prev : {width, height}))
        onResizeRef.current?.({width, height})
      })

      observer.observe(element, box ? {box} : undefined)
      observerRef.current = observer
    },
    [externalRef, box]
  )

  return {ref, width: size.width, height: size.height}
}
